import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, Send, AlertCircle, Briefcase } from 'lucide-react';
import { SectionHeader, FadeUp } from '../components/SectionHeader';
import { CAREER_DOMAINS, IMAGES } from '../data/content';
import { CAREER } from '../constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: '',
  email: '',
  phone: '',
  role: '',
  experience_years: '',
  current_company: '',
  location: '',
  cover_letter: '',
  portfolio_url: '',
};

export default function Careers() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'submitting', message: '' });
    try {
      const payload = {
        ...form,
        experience_years: form.experience_years ? Number(form.experience_years) : null,
      };
      await axios.post(`${API}/careers`, payload);
      setStatus({
        state: 'success',
        message: 'Thank you. Your application has been received. Our team will be in touch.',
      });
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        'Could not submit. Please try again or email us at info@clangrp.com.';
      setStatus({ state: 'error', message: typeof msg === 'string' ? msg : 'Submission failed.' });
    }
  };

  return (
    <div className="bg-white">
      <section className="relative pt-44 pb-20 bg-[#0D1B2A] grain overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${IMAGES.careers})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-[#0D1B2A]/82" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="overline text-white/80 flex items-center gap-3 mb-6">
            <span className="gold-rule" /> Careers
          </div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            Build a career at the <span className="text-[#D4AF37]">engineering frontier.</span>
          </h1>
          <p className="mt-8 text-white/75 text-lg max-w-3xl leading-relaxed">
            Engineering, construction, automation, software, project management and operations —
            join a multidisciplinary team delivering across India, Africa and the Middle East.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader overline="Functions We Hire" title="Where you can grow with us." />
            <div className="mt-10 grid sm:grid-cols-2 gap-3">
              {CAREER_DOMAINS.map((d) => (
                <div
                  key={d}
                  className="border border-[#0D1B2A]/10 px-5 py-4 hover:border-[#D4AF37] transition-colors flex items-center gap-3"
                >
                  <Briefcase size={16} className="text-[#D4AF37]" />
                  <span className="font-display font-semibold text-[#0D1B2A]">{d}</span>
                </div>
              ))}
            </div>

            <FadeUp delay={0.15} className="mt-12 bg-[#F5F7FA] p-7 border-l-2 border-[#D4AF37]">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                Why Clan Group
              </div>
              <ul className="mt-4 space-y-3 text-[#0D1B2A] text-[15px]">
                <li className="flex gap-2.5">
                  <Check size={18} className="text-[#16A34A] shrink-0 mt-0.5" /> Cross-sector
                  exposure: infra, EPC, automation, technology.
                </li>
                <li className="flex gap-2.5">
                  <Check size={18} className="text-[#16A34A] shrink-0 mt-0.5" /> International
                  project deployments across Africa and the Middle East.
                </li>
                <li className="flex gap-2.5">
                  <Check size={18} className="text-[#16A34A] shrink-0 mt-0.5" /> Engineering-first
                  culture with strong HSE standards.
                </li>
              </ul>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-[#0D1B2A]/10 p-7 md:p-10">
              <div className="overline mb-3 flex items-center gap-3">
                <span className="gold-rule" /> Application
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0D1B2A]">
                Send us your details
              </h2>
              <p className="mt-3 text-[#4B5563] text-[14px]">
                We review every application. Tell us where you&apos;d fit.
              </p>

              <form
                onSubmit={onSubmit}
                data-testid={CAREER.form}
                className="mt-8 grid sm:grid-cols-2 gap-5"
              >
                <Field
                  name="name"
                  label="Full Name *"
                  testId={CAREER.name}
                  value={form.name}
                  onChange={onChange}
                  required
                />
                <Field
                  name="email"
                  type="email"
                  label="Email *"
                  testId={CAREER.email}
                  value={form.email}
                  onChange={onChange}
                  required
                />
                <Field
                  name="phone"
                  label="Phone"
                  testId={CAREER.phone}
                  value={form.phone}
                  onChange={onChange}
                />
                <SelectField
                  name="role"
                  label="Role / Function *"
                  testId={CAREER.role}
                  value={form.role}
                  onChange={onChange}
                  options={['', ...CAREER_DOMAINS]}
                  required
                />
                <Field
                  name="experience_years"
                  type="number"
                  step="0.5"
                  min="0"
                  label="Experience (years)"
                  testId={CAREER.experience}
                  value={form.experience_years}
                  onChange={onChange}
                />
                <Field
                  name="current_company"
                  label="Current Company"
                  testId={CAREER.currentCompany}
                  value={form.current_company}
                  onChange={onChange}
                />
                <Field
                  name="location"
                  label="Location"
                  testId={CAREER.location}
                  value={form.location}
                  onChange={onChange}
                />
                <Field
                  name="portfolio_url"
                  label="Portfolio / LinkedIn URL"
                  testId={CAREER.portfolio}
                  value={form.portfolio_url}
                  onChange={onChange}
                  className="sm:col-span-1"
                />
                <div className="sm:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
                    Cover Letter
                  </label>
                  <textarea
                    name="cover_letter"
                    data-testid={CAREER.coverLetter}
                    value={form.cover_letter}
                    onChange={onChange}
                    rows={5}
                    className="w-full border border-[#0D1B2A]/15 px-4 py-3 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none"
                  />
                </div>

                <div className="sm:col-span-2 pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    data-testid={CAREER.submit}
                    disabled={status.state === 'submitting'}
                    className="btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status.state === 'submitting' ? 'Submitting...' : 'Submit Application'}{' '}
                    <Send size={15} />
                  </button>
                  {status.state === 'success' && (
                    <motion.div
                      data-testid={CAREER.success}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="text-sm text-[#16A34A] inline-flex items-center gap-2"
                    >
                      <Check size={16} /> {status.message}
                    </motion.div>
                  )}
                  {status.state === 'error' && (
                    <div
                      data-testid={CAREER.error}
                      className="text-sm text-red-600 inline-flex items-center gap-2"
                    >
                      <AlertCircle size={16} /> {status.message}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ name, label, value, onChange, testId, type = 'text', required, className = '', ...rest }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
        {label}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full border border-[#0D1B2A]/15 px-4 py-3 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none"
        {...rest}
      />
    </div>
  );
}

function SelectField({ name, label, value, onChange, testId, options, required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
        {label}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full border border-[#0D1B2A]/15 px-4 py-3 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none bg-white"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o || 'Select role / function'}
          </option>
        ))}
      </select>
    </div>
  );
}
